import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

// People Logo
const PeopleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="6" r="3.5" />
    <path d="M6 17.5c0-3.5 2.7-6 6-6s6 2.5 6 6v1.5H6v-1.5z" />
    <circle cx="4" cy="8" r="2" opacity="0.6" />
    <path d="M0 15c0-2.2 1.8-4 4-4s4 1.8 4 4v1H0v-1z" opacity="0.6" />
    <circle cx="20" cy="8" r="2" opacity="0.6" />
    <path d="M16 15c0-2.2 1.8-4 4-4s4 1.8 4 4v1h-8v-1z" opacity="0.6" />
  </svg>
);

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request'); // 'request', 'sent', 'reset', 'success'
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(!!token);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Validate token on mount
  useEffect(() => {
    if (token) {
      validateToken();
    }
  }, [token]);

  const validateToken = async () => {
    try {
      const result = await api.verifyResetToken(token);
      if (result.valid) {
        setTokenValid(true);
        setEmail(result.email);
      }
    } catch (error) {
      toast.error(error.message || 'Lien invalide ou expiré');
      setStep('request');
    } finally {
      setValidating(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await api.forgotPassword(email);
      toast.success(result.message);
      setStep('sent');
      
      // If debug token is returned (testing mode), show it
      if (result.debug_token) {
        console.log('Debug reset token:', result.debug_token);
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.resetPassword(token, password);
      toast.success('Mot de passe modifié avec succès');
      setStep('success');
    } catch (error) {
      toast.error(error.message || 'Erreur lors de la réinitialisation');
    } finally {
      setLoading(false);
    }
  };

  if (validating) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-4" />
            <p className="text-gray-600">Vérification du lien...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-xl bg-blue-600 flex items-center justify-center">
              <PeopleIcon className="w-10 h-10 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">
            {step === 'request' && 'Mot de passe oublié'}
            {step === 'sent' && 'Email envoyé'}
            {step === 'reset' && 'Nouveau mot de passe'}
            {step === 'success' && 'Mot de passe modifié'}
          </CardTitle>
          <CardDescription>
            {step === 'request' && 'Entrez votre email pour recevoir un lien de réinitialisation'}
            {step === 'sent' && 'Vérifiez votre boîte de réception'}
            {step === 'reset' && 'Choisissez un nouveau mot de passe'}
            {step === 'success' && 'Vous pouvez maintenant vous connecter'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Request Reset Form */}
          {step === 'request' && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre.email@jeldwen.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <KeyRound className="mr-2 h-4 w-4" />
                    Réinitialiser le mot de passe
                  </>
                )}
              </Button>
              
              <div className="text-center">
                <Link to="/login" className="text-sm text-blue-600 hover:underline inline-flex items-center">
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Retour à la connexion
                </Link>
              </div>
            </form>
          )}

          {/* Email Sent Confirmation */}
          {step === 'sent' && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <p className="text-gray-600">
                Si un compte existe avec l'adresse <strong>{email}</strong>, 
                vous recevrez un email avec les instructions de réinitialisation.
              </p>
              <p className="text-sm text-gray-500">
                Le lien expire dans 1 heure.
              </p>
              <Button variant="outline" onClick={() => setStep('request')} className="mt-4">
                Utiliser une autre adresse
              </Button>
              <div className="mt-4">
                <Link to="/login" className="text-sm text-blue-600 hover:underline inline-flex items-center">
                  <ArrowLeft className="mr-1 h-4 w-4" />
                  Retour à la connexion
                </Link>
              </div>
            </div>
          )}

          {/* Reset Password Form */}
          {step === 'reset' && tokenValid && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
                Réinitialisation pour : <strong>{email}</strong>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Nouveau mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Modification...
                  </>
                ) : (
                  <>
                    <KeyRound className="mr-2 h-4 w-4" />
                    Modifier le mot de passe
                  </>
                )}
              </Button>
            </form>
          )}

          {/* Success */}
          {step === 'success' && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
              </div>
              <p className="text-gray-600">
                Votre mot de passe a été modifié avec succès.
              </p>
              <Button onClick={() => navigate('/login')} className="w-full">
                Se connecter
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
